// Base analyzer class for Excel data analysis
// Provides common interface and shared functionality for all analyzers

/**
 * Base class for all data analyzers
 * Provides common interface and shared functionality
 */
export class BaseAnalyzer {
  constructor(analyzerType) {
    this.analyzerType = analyzerType;
  }

  /**
   * Analyze data - to be implemented by subclasses
   * @param {Array} records - Records to analyze
   * @returns {Object} - Analysis results
   */
  analyze(records) {
    throw new Error("analyze method must be implemented by subclass");
  }

  /**
   * Generate alerts - to be implemented by subclasses
   * @param {Array} records - Records to generate alerts for
   * @returns {Array} - Generated alerts
   */
  generateAlerts(records) {
    throw new Error("generateAlerts method must be implemented by subclass");
  }

  /**
   * Create alert object with standard structure
   * @param {string} type - Alert type (danger, warning, info, success)
   * @param {string} category - Alert category
   * @param {string} message - Alert message
   * @param {number} count - Count for the alert
   * @param {string} priority - Priority level
   * @param {*} details - Additional details
   * @returns {Object} - Formatted alert object
   */
  createAlert(type, category, message, count = 0, priority = 'medium', details = null) {
    return {
      type,
      category,
      message,
      count,
      priority,
      details,
    };
  }

  /**
   * Safe division helper to avoid divide by zero
   * @param {number} numerator - Number to divide
   * @param {number} denominator - Number to divide by
   * @param {number} precision - Decimal places (default: 2)
   * @returns {number} - Result or 0 if denominator is 0
   */
  safeDivide(numerator, denominator, precision = 2) {
    if (denominator === 0) return 0;
    return Math.round((numerator / denominator) * Math.pow(10, precision)) / Math.pow(10, precision);
  }

  /**
   * Group records by a specific field
   * @param {Array} records - Records to group
   * @param {string} field - Field to group by
   * @param {string} defaultValue - Default value for missing fields
   * @returns {Object} - Grouped records
   */
  groupBy(records, field, defaultValue = 'Unknown') {
    const grouped = {};
    records.forEach(record => {
      const key = record[field] || defaultValue;
      grouped[key] = (grouped[key] || 0) + 1;
    });
    return grouped;
  }

  /**
   * Calculate basic statistics for numeric values
   * @param {Array} values - Numeric values
   * @returns {Object} - Statistics object
   */
  calculateStats(values) {
    if (values.length === 0) {
      return { total: 0, average: 0, min: 0, max: 0, count: 0 };
    }

    const total = values.reduce((sum, val) => sum + val, 0);
    const average = total / values.length;
    const min = Math.min(...values);
    const max = Math.max(...values);

    return {
      total: this.safeDivide(total, 1),
      average: this.safeDivide(total, values.length),
      min,
      max,
      count: values.length,
    };
  }

  /**
   * Log analysis start
   * @param {string} analysisType - Type of analysis
   * @param {number} recordCount - Number of records being analyzed
   */
  logAnalysisStart(analysisType, recordCount) {
    console.log(`📊 Analyzing ${recordCount} ${analysisType} records`);
  }

  /**
   * Log analysis completion
   * @param {string} analysisType - Type of analysis
   */
  logAnalysisComplete(analysisType) {
    console.log(`  ✅ ${analysisType} analysis complete`);
  }
} 